/**
 * Admin — Field Activity Review Queue
 * Route: /admin/field-activities/review
 * Phase 2D P0 (2026-04-24)
 *
 * READS: GET /admin/field-activities/review-queue?state=...
 * Actions (navigate to detail): claim / approve / reject / return / escalate
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Loader2, ArrowLeft, Inbox, User, MapPin, Camera, AlertTriangle, Clock } from "lucide-react";

const STATES = [
  ["pending", "Pending"],
  ["under_review", "Under review"],
  ["returned_to_agent", "Returned"],
  ["escalated", "Escalated"],
  ["approved", "Approved"],
  ["rejected", "Rejected"],
];

function reviewBadgeClass(state) {
  switch (state) {
    case "pending": return "bg-amber-500 text-white";
    case "under_review": return "bg-indigo-600 text-white";
    case "approved": return "bg-emerald-600 text-white";
    case "rejected": return "bg-rose-600 text-white";
    case "returned_to_agent": return "bg-slate-500 text-white";
    case "escalated": return "bg-fuchsia-600 text-white";
    default: return "bg-slate-400 text-white";
  }
}

export default function AdminFieldActivityReviewQueue() {
  const navigate = useNavigate();
  const [state, setState] = useState("pending");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/field-activities/review-queue?state=${encodeURIComponent(state)}&limit=100`);
      setRows(res.data.rows || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to load queue");
    } finally { setLoading(false); }
  }, [state]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-slate-50 pb-10" data-testid="admin-review-queue-page">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-5">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="review-queue-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Inbox className="w-6 h-6 text-indigo-600" /> Field Activity Review Queue
            </h1>
            <p className="text-xs text-slate-500">Claim a row to begin review. Approved activities are soft-locked — admin-override edits require a reason.</p>
          </div>
          <Badge variant="outline" data-testid="review-queue-total">{total} in {state}</Badge>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-2 mb-4 flex flex-wrap gap-2" data-testid="review-queue-tabs">
          {STATES.map(([s, label]) => (
            <button
              key={s}
              onClick={() => setState(s)}
              data-testid={`review-queue-tab-${s}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${state === s ? "bg-slate-900 text-white border-slate-900" : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100"}`}
            >{label}</button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="review-queue-empty">
            No activities in this state.
          </div>
        ) : (
          <div className="grid gap-2" data-testid="review-queue-rows">
            {rows.map((r) => {
              const evCount = (r.evidence || []).length;
              const dup = r.duplicate_signal?.score || 0;
              return (
                <div
                  key={r.id}
                  data-testid={`review-queue-row-${r.id}`}
                  onClick={() => navigate(`/admin/field-agents/${r.agent_id}/activities/${r.id}`)}
                  className="bg-white rounded-xl border border-slate-200 px-4 py-3 cursor-pointer hover:border-indigo-300 hover:shadow-sm transition"
                >
                  <div className="flex items-center gap-3">
                    <Badge className={reviewBadgeClass(r.review?.state)}>{r.review?.state || "—"}</Badge>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">
                        <User className="w-3 h-3 inline mr-1 text-slate-400" />
                        {r.agent_id} · {r.prospect?.name || "—"} ({r.prospect?.type})
                      </div>
                      <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                        <span><Clock className="w-3 h-3 inline mr-0.5" />{r.activity_date}</span>
                        <span><MapPin className="w-3 h-3 inline mr-0.5" />{r.parish} · {r.area}</span>
                        <span><Camera className="w-3 h-3 inline mr-0.5" />{evCount}</span>
                        {dup > 0 && <span className="text-amber-600"><AlertTriangle className="w-3 h-3 inline mr-0.5" />dup {dup.toFixed(2)}</span>}
                        {r.ownership_conflict && <span className="text-rose-600">ownership-conflict</span>}
                        {r.territory_warning && <span className="text-indigo-600">territory-flag</span>}
                      </div>
                    </div>
                    {r.review?.reviewer_id && (
                      <Badge variant="outline" className="text-xs">reviewer: {r.review.reviewer_id}</Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
